import { Body } from '@assembly-js/design-system';
import Dialog from '@mui/material/Dialog';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { PLAN_PAGE } from 'src/constants';
import { PortalConfigContext, RouteContext } from 'src/context';
import history from 'src/history';
import { useAppSelector } from 'src/hooks/useStore';
import { additionalWorkspaceDecision } from 'src/routes/onboarding/setup/utils/additionalWorkspaceAllowed';
import { ADDITIONAL_WORKSPACE_PLAN_PARAM } from 'src/routes/settings/plans/additionalWorkspacePlan';
import checkIcon from 'src/routes/settings/plans/components/additional-workspace/check.svg';
import chevronIcon from 'src/routes/settings/plans/components/additional-workspace/chevron.svg';
import closeIcon from 'src/routes/settings/plans/components/additional-workspace/close.svg';
import { ProxyPurchaseNotice } from 'src/routes/settings/plans/components/ProxyPurchaseGuard';
import { PlanChangeUpgradeModal } from 'src/routes/settings/plans/components/studio/PlanChangeUpgradeModal';
import { getStudioPlanTitle } from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import {
  formatDollars,
  monthlyPriceCents,
  StudioBillingInterval,
  StudioPlanSelection,
} from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import { useStudioPlanChange } from 'src/routes/settings/plans/components/studio/useStudioPlanChange';
import { useGetStudioPlansQuery } from 'src/services/api';
import { StudioPlanId } from 'src/services/api/studioCreditApi';
import { readIsProxySession } from 'src/utils/proxySession';

const MODAL_PLANS: {
  id: Extract<StudioPlanId, 'starter' | 'professional' | 'advanced'>;
  fallbackMonthlyCents: number;
  emphasized: boolean;
}[] = [
  { id: 'starter', fallbackMonthlyCents: 7900, emphasized: false },
  { id: 'professional', fallbackMonthlyCents: 9900, emphasized: true },
  { id: 'advanced', fallbackMonthlyCents: 49900, emphasized: false },
];

const hasAdditionalWorkspaceParam = (
  query: Record<string, string>,
): boolean => {
  if (query[ADDITIONAL_WORKSPACE_PLAN_PARAM] === '1') {
    return true;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    new URLSearchParams(window.location.search).get(
      ADDITIONAL_WORKSPACE_PLAN_PARAM,
    ) === '1'
  );
};

const clearAdditionalWorkspaceParam = () => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(ADDITIONAL_WORKSPACE_PLAN_PARAM)) {
    return;
  }
  params.delete(ADDITIONAL_WORKSPACE_PLAN_PARAM);
  const search = params.toString();
  // history.location.pathname is already basename-free; window.location.pathname
  // is not and would double up the workspace prefix.
  history.replace({
    pathname: history.location.pathname,
    search: search ? `?${search}` : '',
  });
};

export const AdditionalWorkspaceUpgradeModal = () => {
  const { query } = React.useContext(RouteContext);
  const portalConfig = React.useContext(PortalConfigContext);
  const email = useAppSelector(
    (state) => state.user.instance?.attributes?.email,
  );
  const [dismissed, setDismissed] = React.useState(false);
  const [blocked, setBlocked] = React.useState(false);
  const requested = !dismissed && hasAdditionalWorkspaceParam(query);

  React.useEffect(() => {
    if (!requested) {
      setBlocked(false);
      return;
    }
    const trimmed = email?.trim() ?? '';
    if (!trimmed) {
      setBlocked(false);
      return;
    }
    let cancelled = false;
    void additionalWorkspaceDecision(portalConfig, trimmed).then((decision) => {
      if (cancelled) {
        return;
      }
      // A paid workspace anywhere on this email means another workspace is
      // allowed, so this modal must stay closed.
      if (decision.allowed) {
        clearAdditionalWorkspaceParam();
        setBlocked(false);
        return;
      }
      setBlocked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [requested, email, portalConfig]);

  if (!requested || !blocked) {
    return null;
  }
  return (
    <AdditionalWorkspaceUpgradeDialog
      onDismiss={() => {
        setDismissed(true);
        clearAdditionalWorkspaceParam();
      }}
    />
  );
};

const AdditionalWorkspaceUpgradeDialog = ({
  onDismiss,
}: {
  onDismiss: () => void;
}) => {
  const { t } = useTranslation();
  const isClient = useAppSelector((state) => state.user.isClient);
  const [interval, setInterval] =
    React.useState<StudioBillingInterval>('month');
  const { data } = useGetStudioPlansQuery(undefined, { skip: isClient });
  const {
    selection,
    activeModal,
    isUpgrading,
    choosePlan,
    confirmUpgrade,
    closeModals,
  } = useStudioPlanChange();

  const open = !isClient;

  const close = () => {
    onDismiss();
  };

  const subtitleFor = (id: (typeof MODAL_PLANS)[number]['id']): string => {
    if (id === 'starter') {
      return t(
        ($) => $.page_studio_plans.additional_workspace.starter_subtitle,
      );
    }
    if (id === 'professional') {
      return t(($) => $.page_studio_plans.additional_workspace.pro_subtitle);
    }
    return t(($) => $.page_studio_plans.additional_workspace.advanced_subtitle);
  };

  const workspacesFor = (id: (typeof MODAL_PLANS)[number]['id']): string => {
    if (id === 'starter') {
      return t(
        ($) => $.page_studio_plans.additional_workspace.starter_workspaces,
      );
    }
    if (id === 'professional') {
      return t(($) => $.page_studio_plans.additional_workspace.pro_workspaces);
    }
    return t(
      ($) => $.page_studio_plans.additional_workspace.advanced_workspaces,
    );
  };

  const detailFor = (id: (typeof MODAL_PLANS)[number]['id']): string => {
    if (id === 'starter') {
      return t(($) => $.page_studio_plans.feat.active_contacts.starter);
    }
    if (id === 'professional') {
      return t(($) => $.page_studio_plans.feat.active_contacts.pro);
    }
    return t(($) => $.page_studio_plans.feat.active_contacts.advanced);
  };

  const priceFor = (
    id: (typeof MODAL_PLANS)[number]['id'],
    fallbackMonthlyCents: number,
  ): string => {
    const tier = data?.plans.find((plan) => plan.plan === id)?.tiers.at(0);
    const cents = tier
      ? monthlyPriceCents(tier, interval)
      : fallbackMonthlyCents;
    return formatDollars(cents);
  };

  const isProxyPurchaseBlocked = readIsProxySession();
  const upgrade = (id: (typeof MODAL_PLANS)[number]['id']) => {
    if (isProxyPurchaseBlocked) {
      return;
    }
    const tier = data?.plans.find((plan) => plan.plan === id)?.tiers.at(0);
    if (!tier) {
      history.push(PLAN_PAGE.path);
      return;
    }
    const chosen: StudioPlanSelection = {
      planId: id,
      planTitle: getStudioPlanTitle(id),
      interval,
      tier: {
        credits: tier.credits,
        monthlyCents: tier.monthlyCents,
        annualCents: tier.annualCents,
        includedCredits: tier.credits,
      },
    };
    choosePlan(chosen);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        maxWidth={false}
        aria-labelledby="additional-workspace-upgrade-title"
        PaperProps={{
          sx: {
            width: 'min(878px, calc(100vw - 32px))',
            maxWidth: 'none',
            borderRadius: '8px',
            margin: '16px',
            backgroundColor: '#fff',
            color: '#212B36',
            backgroundImage: 'none',
          },
        }}
      >
        <div className="relative flex flex-col items-center gap-6 bg-white px-6 pb-6 pt-6 font-[Inter,sans-serif] text-black-heading">
          <button
            type="button"
            className="absolute right-2 top-2 m-0 flex size-6 cursor-pointer appearance-none items-center justify-center rounded-full border-0 bg-gray-100 p-0"
            aria-label={t(
              ($) => $.page_studio_plans.additional_workspace.close,
            )}
            onClick={close}
          >
            <img src={closeIcon} alt="" />
          </button>
          <div className="flex w-full flex-col items-center gap-1 text-center">
            <p
              id="additional-workspace-upgrade-title"
              className="m-0 text-base font-medium leading-6 text-black-heading"
            >
              {t(($) => $.page_studio_plans.additional_workspace.modal_title)}
            </p>
            <Body
              size="sm"
              className="mb-0 w-[417px] max-w-full text-center text-sm font-normal leading-[22px] text-[#6a7282]"
            >
              {t(($) => $.page_studio_plans.additional_workspace.modal_body)}
            </Body>
          </div>
          <div className="flex items-center gap-2 rounded border border-solid border-non-hover-border bg-white p-1">
            <IntervalTab
              label={t(($) => $.page_studio_plans.additional_workspace.monthly)}
              selected={interval === 'month'}
              onSelect={() => setInterval('month')}
            />
            <IntervalTab
              label={t(($) => $.page_studio_plans.additional_workspace.yearly)}
              selected={interval === 'year'}
              onSelect={() => setInterval('year')}
            />
          </div>
          <ProxyPurchaseNotice />
          <div className="flex w-full flex-col items-stretch gap-5 md:flex-row">
            {MODAL_PLANS.map((plan) => (
              <div
                key={plan.id}
                className="flex min-w-0 flex-1 flex-col gap-4 rounded border border-solid border-non-hover-border bg-white p-5 md:h-[316px]"
              >
                <div className="flex flex-col gap-0.5">
                  <p className="m-0 text-base font-medium leading-6 text-black-heading">
                    {plan.id === 'professional'
                      ? t(
                          ($) =>
                            $.page_studio_plans.additional_workspace.pro_title,
                        )
                      : getStudioPlanTitle(plan.id)}
                  </p>
                  <p className="m-0 text-[13px] font-normal leading-[21px] text-gray-small">
                    {subtitleFor(plan.id)}
                  </p>
                </div>
                <div className="flex flex-col gap-0.5">
                  <p className="m-0 text-[13px] font-normal leading-[21px] text-gray-small">
                    {t(
                      ($) =>
                        $.page_studio_plans.additional_workspace.starting_at,
                    )}
                  </p>
                  <p className="m-0 flex items-baseline gap-[5px] text-black-heading">
                    <span className="text-2xl font-medium leading-8">
                      {priceFor(plan.id, plan.fallbackMonthlyCents)}
                    </span>
                    <span className="text-[13px] font-normal leading-[21px]">
                      {t(
                        ($) =>
                          $.page_studio_plans.additional_workspace.per_month,
                      )}
                    </span>
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <FeatureLine label={workspacesFor(plan.id)} />
                  <FeatureLine label={detailFor(plan.id)} />
                </div>
                <div className="mt-auto flex flex-col gap-2">
                  <button
                    type="button"
                    disabled={isProxyPurchaseBlocked}
                    className={
                      plan.emphasized
                        ? 'm-0 box-border w-full cursor-pointer appearance-none rounded border border-solid border-black/10 bg-black-heading px-[9px] py-1 text-center font-[Inter,sans-serif] text-xs font-medium leading-5 text-white disabled:cursor-not-allowed disabled:opacity-50'
                        : 'm-0 box-border w-full cursor-pointer appearance-none rounded border border-solid border-non-hover-border bg-white px-[9px] py-1 text-center font-[Inter,sans-serif] text-xs font-medium leading-5 text-black-heading disabled:cursor-not-allowed disabled:opacity-50'
                    }
                    onClick={() => upgrade(plan.id)}
                  >
                    {plan.emphasized
                      ? t(
                          ($) =>
                            $.page_studio_plans.additional_workspace
                              .upgrade_named,
                          { plan: 'Pro' },
                        )
                      : t(
                          ($) =>
                            $.page_studio_plans.additional_workspace.upgrade,
                        )}
                  </button>
                  <button
                    type="button"
                    className="m-0 box-border flex w-full cursor-pointer appearance-none items-center justify-center gap-1.5 rounded border-0 bg-transparent px-2 py-1 font-[Inter,sans-serif] text-xs font-medium leading-5 text-black-heading"
                    onClick={() => {
                      close();
                      history.push(PLAN_PAGE.path);
                    }}
                  >
                    {t(
                      ($) =>
                        $.page_studio_plans.additional_workspace.see_details,
                    )}
                    <img src={chevronIcon} alt="" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Dialog>
      {selection && (
        <PlanChangeUpgradeModal
          isOpen={activeModal === 'upgrade'}
          creditLadder={data?.creditLadder ?? []}
          selection={selection}
          isLoading={isUpgrading}
          onClose={closeModals}
          onConfirm={(totalCredits) => {
            void confirmUpgrade(totalCredits);
          }}
        />
      )}
    </>
  );
};

const IntervalTab = ({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    className={
      selected
        ? 'm-0 cursor-pointer appearance-none rounded-sm border-0 bg-gray-150 px-1.5 py-0.5 font-[Inter,sans-serif] text-xs font-normal leading-5 text-black-heading'
        : 'm-0 cursor-pointer appearance-none rounded-sm border-0 bg-transparent px-1.5 py-0.5 font-[Inter,sans-serif] text-xs font-normal leading-5 text-black-heading'
    }
    onClick={onSelect}
  >
    {label}
  </button>
);

const FeatureLine = ({ label }: { label: string }) => (
  <div className="flex items-start gap-1.5">
    <span className="flex h-[21px] items-center">
      <img src={checkIcon} alt="" />
    </span>
    <span className="text-[13px] font-normal leading-[21px] text-gray-small">
      {label}
    </span>
  </div>
);
